/**
 * Theme preview page (ENG-222) — originally a stakeholder preview comparing the
 * proposed orange brand theme (#C16A2C) side-by-side with the red theme it
 * replaced. The global token swap (ENG-223) has since shipped, so both panels
 * now render the same orange the app uses everywhere; this page is kept as a
 * historical record of the rebrand mapping.
 *
 * The right-hand panel is driven ONLY by scoped CSS custom properties set
 * inline on its wrapper div — index.css and the global theme are intentionally
 * untouched by this page.
 */

import type { CSSProperties, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * ORANGE_THEME_MAPPING — the historical record of the red → orange rebrand,
 * anchored on #C16A2C. Applied by:
 *   - ENG-223 (global CSS token swap in index.css `:root`) — shipped
 *   - ENG-224 (logo asset regeneration) — shipped
 *
 * Key semantics:
 *   - Keys beginning with `--` are the five CSS custom properties that were
 *     #E11837 in index.css `:root` prior to ENG-223; the value is their orange
 *     replacement, now live. (--destructive and --chart-5 stay #EF4444 —
 *     semantic red, deliberately NOT part of this mapping.)
 *   - Keys beginning with `#` are the six red stops that were in
 *     packages/web/public/compbox-logo.svg before ENG-224 replaced it with the
 *     orange logo; the value is the orange ramp replacement. #C0102D was the
 *     primary face and maps to the #C16A2C anchor itself.
 *   - `hover` / `tint` are derived interaction shades of the anchor
 *     (hover ≈ anchor darkened ~15%, tint ≈ anchor lightened for subtle fills).
 */
export const ORANGE_THEME_MAPPING: Record<string, string> = {
  // --- CSS custom properties, formerly #E11837 in index.css :root ---
  "--primary": "#C16A2C",
  "--ring": "#C16A2C",
  "--chart-1": "#C16A2C",
  "--sidebar-primary": "#C16A2C",
  "--sidebar-ring": "#C16A2C",

  // --- Logo ramp stops in compbox-logo.svg (red → orange) ---
  "#C0102D": "#C16A2C", // top-left face → anchor
  "#FF2D50": "#FF9D5C", // top-right face (brightest highlight)
  "#981525": "#8A4A1F", // right face, upper
  "#731020": "#66351A", // right face, lower
  "#6B0A1A": "#5E3013", // left face, upper
  "#520818": "#4A250E", // left face, lower (darkest)

  // --- Derived interaction shades of the anchor ---
  hover: "#A05623",
  tint: "#E89150",
};

/** The five scoped variables applied to the orange panel wrapper. */
const orangeScopeStyle: CSSProperties = {
  "--primary": ORANGE_THEME_MAPPING["--primary"],
  "--ring": ORANGE_THEME_MAPPING["--ring"],
  "--chart-1": ORANGE_THEME_MAPPING["--chart-1"],
  "--sidebar-primary": ORANGE_THEME_MAPPING["--sidebar-primary"],
  "--sidebar-ring": ORANGE_THEME_MAPPING["--sidebar-ring"],
} as CSSProperties;

const CHART_VARS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"] as const;

function SampleSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The shared sample content rendered in both panels. Everything that references
 * --primary / --ring / --chart-1 / --sidebar-primary / --sidebar-ring picks up
 * the scoped overrides when rendered inside the orange wrapper.
 */
function ThemeSamples() {
  return (
    <div className="space-y-6">
      <SampleSection title="Buttons">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="default">Default</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          {/* Button primitives are glass-styled; this sample shows the raw
              primary token the way CTAs and accents consume it. */}
          <button
            type="button"
            className={cn(
              "inline-flex h-9 items-center rounded-xl bg-primary px-4 text-xs font-medium",
              "text-primary-foreground transition-all hover:opacity-90",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            )}
          >
            Primary action
          </button>
        </div>
      </SampleSection>

      <SampleSection title="Destructive alert (semantic red — intentionally unchanged)">
        <div
          role="alert"
          className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
        >
          Something went wrong. Destructive/error red stays #EF4444 in both
          themes — it is a semantic color, not a brand color.
        </div>
      </SampleSection>

      <SampleSection title="Card">
        <div className="glass-card rounded-2xl p-4">
          <h4 className="text-sm font-semibold text-foreground">Glass card heading</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            Card surfaces are neutral; only the accent ring below picks up the
            brand color.
          </p>
          <div className="mt-3 h-1 w-16 rounded-full bg-primary" />
        </div>
      </SampleSection>

      <SampleSection title="Sidebar nav item (active)">
        <div
          className="flex w-full max-w-xs items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium"
          style={{
            background: "var(--sidebar-accent)",
            color: "var(--sidebar-accent-foreground)",
          }}
        >
          <span
            aria-hidden
            className="h-4 w-1 rounded-full"
            style={{ background: "var(--sidebar-primary)" }}
          />
          <span>Dashboard</span>
          <span
            className="ml-auto rounded-md px-1.5 py-0.5 text-[10px] font-semibold"
            style={{
              background: "var(--sidebar-primary)",
              color: "var(--sidebar-primary-foreground)",
            }}
          >
            3
          </span>
        </div>
      </SampleSection>

      <SampleSection title="Chart swatches (--chart-1 … --chart-5)">
        <div className="flex items-center gap-2">
          {CHART_VARS.map((varName) => (
            <div key={varName} className="space-y-1 text-center">
              <div
                className="h-8 w-8 rounded-md border border-white/10"
                style={{ background: `var(${varName})` }}
                title={varName}
              />
              <div className="text-[10px] text-muted-foreground">{varName.slice(2)}</div>
            </div>
          ))}
        </div>
      </SampleSection>
    </div>
  );
}

function PreviewPanel({
  heading,
  subheading,
  children,
}: {
  heading: string;
  subheading: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 md:p-6">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-foreground">{heading}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{subheading}</p>
      </div>
      {children}
    </div>
  );
}

export function ThemePreviewPage() {
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8">
      <header>
        <h1 className="text-xl font-semibold text-foreground">Theme preview</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Historical record of the orange rebrand (#C16A2C): the live global
          theme on the left, next to the scoped preview panel used to pitch it.
          Both now render orange since the rebrand shipped. The right panel is
          driven by scoped CSS variables only — the global theme is unchanged.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PreviewPanel
          heading="Current theme"
          subheading="Live global tokens, as shipped."
        >
          <ThemeSamples />
        </PreviewPanel>

        <div data-testid="orange-panel-scope" style={orangeScopeStyle}>
          <PreviewPanel
            heading="Proposed orange (#C16A2C)"
            subheading="Same components with --primary, --ring, --chart-1, --sidebar-primary and --sidebar-ring scoped to orange."
          >
            <ThemeSamples />
          </PreviewPanel>
        </div>
      </div>
    </div>
  );
}
